import i18n from "i18next";
import { Box, Card, CardContent, Stack, Typography } from "@mui/material";
import { useRef, useState } from "react";
import { AavoMarkdown } from "src/components/common/AavoMarkdown.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import { AavoFormActionButtons } from "src/components/common/forms/AavoFormActionButtons.tsx";
import { AavoFormFooter } from "src/components/common/forms/AavoFormFooter.tsx";
import {
	JsonSchemaObjectForm,
	type JsonSchemaObjectFormRef,
} from "src/components/common/forms/jsonSchema/JsonSchemaObjectForm.tsx";
import {
	createInitialJsonSchemaObject,
	type JsonObject,
	toJsonObject,
	toRjsfSchema,
} from "src/components/common/forms/jsonSchema/jsonSchemaObjectInputUtils.ts";
import {
	createChoiceButtonSupplementalSchema,
	createUserInputRequestUiSchema,
	matchesChoiceButtonResponse,
	mergeChoiceButtonResponse,
	type UserInputRequestPresentation,
} from "src/components/common/userInputRequests/userInputRequestPresentation.ts";
import type { IsoDateTimeString } from "src/types/dateTime.ts";
import { formatIsoString } from "src/utils/dayjsUtils.ts";

interface UserInputRequestCardProps {
	userInputRequestId: number;
	prompt: string;
	responseSchema: unknown;
	presentation: UserInputRequestPresentation;
	response?: JsonObject;
	expiresAt?: IsoDateTimeString | null;
	disabled?: boolean;
	displayMode?: "card" | "dialog";
	onCancel?: () => Promise<unknown>;
	onRespond: (response: JsonObject) => Promise<unknown>;
}

type UserInputRequestCardContentProps = Omit<UserInputRequestCardProps, "userInputRequestId">;

export function UserInputRequestCard({
	userInputRequestId,
	...props
}: UserInputRequestCardProps) {
	return <UserInputRequestCardContent key={userInputRequestId} {...props} />;
}

function UserInputRequestCardContent({
	prompt,
	responseSchema,
	presentation,
	response,
	expiresAt,
	disabled = false,
	displayMode = "card",
	onCancel,
	onRespond,
}: UserInputRequestCardContentProps) {
	const schema = toRjsfSchema(responseSchema);
	const supplementalSchema =
		presentation.type === "choice-buttons" ?
			createChoiceButtonSupplementalSchema(schema, presentation.fieldControls, presentation.options)
		: undefined;
	const displayedSchema = presentation.type === "form" ? schema : supplementalSchema;
	const [formData, setFormData] = useState(() =>
		createInitialJsonSchemaObject(displayedSchema ?? { type: "object" }, response),
	);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const formRef = useRef<JsonSchemaObjectFormRef | null>(null);
	const isReadOnly = disabled || response != null;
	const displayedFormData = response ?? formData;
	const choiceButtons =
		presentation.type === "choice-buttons" ?
			presentation.options.map((option) => (
				<AsyncButton
					key={option.id}
					variant={matchesChoiceButtonResponse(option.value, response) ? "contained" : "outlined"}
					label={option.label}
					disabled={isReadOnly || isSubmitting}
					onClick={() => submitChoice(option.value)}
				/>
			))
		: null;

	const content = (
		<Stack spacing={2}>
			<AavoMarkdown sx={{ flex: "none" }}>{prompt}</AavoMarkdown>
			{expiresAt != null && response == null && (
				<Typography variant="body2" color="text.secondary">
					{i18n.t("user_input_request_expires_at", { time: formatIsoString(expiresAt, "L LT") })}
				</Typography>
			)}
			{displayedSchema != null && (
				<JsonSchemaObjectForm
					ref={formRef}
					schema={displayedSchema}
					uiSchema={createUserInputRequestUiSchema(presentation.fieldControls)}
					formData={displayedFormData}
					readOnly={isReadOnly}
					onChange={(value) => setFormData(toJsonObject(value))}
				/>
			)}
			{displayMode === "card" && (
				<Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap", justifyContent: "flex-end" }}>
					{!isReadOnly && onCancel != null && (
						<AsyncButton
							color="secondary"
							label={i18n.t("cancel")}
							disabled={isSubmitting}
							onClick={cancelRequest}
						/>
					)}
					{!isReadOnly && presentation.type === "form" && (
						<AsyncButton
							variant="contained"
							label={i18n.t("respond")}
							disabled={isSubmitting}
							onClick={submitForm}
						/>
					)}
					{presentation.type === "choice-buttons" && choiceButtons}
				</Stack>
			)}
		</Stack>
	);

	if (displayMode === "dialog") {
		return (
			<VerticalBox sx={{ flex: 1, minWidth: 0, minHeight: 0 }} onKeyDown={handleKeyDown}>
				<Box sx={{ flex: 1, minWidth: 0, minHeight: 0, overflow: "auto", padding: 2 }}>{content}</Box>
				{!isReadOnly && presentation.type === "form" && (
					<AavoFormActionButtons
						onCancel={onCancel == null ? undefined : cancelRequest}
						onSubmit={submitForm}
						submitLabel={i18n.t("respond")}
						disableSubmit={isSubmitting}
					/>
				)}
				{!isReadOnly && presentation.type === "choice-buttons" && (
					<AavoFormFooter sx={{ flexWrap: "wrap", paddingY: 1 }}>{choiceButtons}</AavoFormFooter>
				)}
			</VerticalBox>
		);
	}

	return (
		<Card variant="outlined" sx={{ width: "100%", minWidth: 0 }} onKeyDown={handleKeyDown}>
			<CardContent>{content}</CardContent>
		</Card>
	);

	async function submitForm() {
		if (isReadOnly || isSubmitting) return;
		if (formRef.current?.validate() !== true) return;
		await submitResponse(formData);
	}

	async function submitChoice(optionValue: JsonObject) {
		if (isReadOnly || isSubmitting) return;
		if (supplementalSchema != null && formRef.current?.validate() !== true) return;
		await submitResponse(mergeChoiceButtonResponse(optionValue, formData));
	}

	async function submitResponse(value: JsonObject) {
		setIsSubmitting(true);
		try {
			await onRespond(value);
		} finally {
			setIsSubmitting(false);
		}
	}

	async function cancelRequest() {
		if (isReadOnly || isSubmitting || onCancel == null) return;
		setIsSubmitting(true);
		try {
			await onCancel();
		} finally {
			setIsSubmitting(false);
		}
	}

	async function handleKeyDown(event: React.KeyboardEvent<HTMLElement>) {
		if (event.key !== "Enter" || !event.ctrlKey || presentation.type !== "form") return;
		event.preventDefault();
		await submitForm();
	}
}
